import type { InstrumentCreateRequest, InstrumentPublic, InstrumentUpdateRequest } from '@/types';

const padTwo = (n: number) => String(n).padStart(2, '0');

export interface InstrumentFormState {
  name: string;
  serial_num: string;
  model: string;
  is_available: boolean;
  /** Local datetime string in YYYY-MM-DDTHH:mm format, or empty string when unset */
  next_pm_at: string;
}

export const createEmptyFormState = (): InstrumentFormState => ({
  name: '',
  serial_num: '',
  model: '',
  is_available: true,
  next_pm_at: '',
});

/**
 * Convert InstrumentPublic to form state.
 * next_pm_at is stored as YYYY-MM-DDTHH:mm for use with <input type="datetime-local">.
 * The incoming API value is an ISO string (UTC); we convert to local time so the
 * input reflects the user's timezone rather than always showing midnight UTC.
 */
export const convertInstrumentToFormState = (instrument: InstrumentPublic): InstrumentFormState => {
  let nextPmAt = '';
  if (instrument.next_pm_at) {
    const d = new Date(instrument.next_pm_at);
    // Format as YYYY-MM-DDTHH:mm in local time
    nextPmAt = `${d.getFullYear()}-${padTwo(d.getMonth() + 1)}-${padTwo(d.getDate())}T${padTwo(d.getHours())}:${padTwo(d.getMinutes())}`;
  }
  return {
    name: instrument.name,
    serial_num: instrument.serial_num ?? '',
    model: instrument.model ?? '',
    is_available: instrument.is_available,
    next_pm_at: nextPmAt,
  };
};

/**
 * Convert a YYYY-MM-DDTHH:mm local datetime string to an ISO 8601 UTC string.
 * Uses the Date(y,m,d,h,min) constructor to guarantee local-time interpretation
 * across all environments — `new Date(str)` is inconsistent for strings without
 * a timezone designator in some runtimes.
 * Returns null for empty/falsy input or strings that don't produce a valid date.
 */
export const dateStringToISOString = (dateStr: string): string | null => {
  if (!dateStr) return null;
  const [datePart, timePart = '00:00'] = dateStr.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  const d = new Date(year, month - 1, day, hours, minutes);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

/**
 * Convert form state to InstrumentCreateRequest body
 */
export const buildCreateRequest = (form: InstrumentFormState): InstrumentCreateRequest => ({
  name: form.name.trim(),
  serial_num: form.serial_num.trim() || null,
  model: form.model.trim() || null,
  is_available: form.is_available,
  next_pm_at: dateStringToISOString(form.next_pm_at),
  // Instruments belong to orgs via an assignment table now: reads require is_visible, writes
  // require is_current, and at most one org may be is_current for a given instrument.
  is_current: true,
  is_visible: true,
});

/**
 * Convert form state to InstrumentUpdateRequest body
 */
export const buildUpdateRequest = (form: InstrumentFormState): InstrumentUpdateRequest => ({
  name: form.name.trim(),
  serial_num: form.serial_num.trim() || null,
  model: form.model.trim() || null,
  is_available: form.is_available,
  next_pm_at: dateStringToISOString(form.next_pm_at),
  // Instruments belong to orgs via an assignment table now: reads require is_visible, writes
  // require is_current, and at most one org may be is_current for a given instrument.
  is_current: true,
  is_visible: true,
});
